import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
  Snackbar,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as ApplyIcon,
  Public as GlobeIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsApi } from '../../api/client';
import { Segment, GeoRules, GeoOptions, GeoCityOption } from '../../types';
import { countryLabel } from '../../utils/countries';

interface SegmentForm {
  name: string;
  description: string;
  color: string;
  isDynamic: boolean;
  originCountries: string[];
  destinationCountries: string[];
  originCities: string[];
  destinationCities: string[];
  match: 'any' | 'all';
  monthsBack: string;
}

const emptyForm: SegmentForm = {
  name: '',
  description: '',
  color: '#1976d2',
  isDynamic: false,
  originCountries: [],
  destinationCountries: [],
  originCities: [],
  destinationCities: [],
  match: 'any',
  monthsBack: '',
};

function formFromSegment(segment: Segment): SegmentForm {
  const r: GeoRules = segment.geoRules || {};
  return {
    name: segment.name,
    description: segment.description || '',
    color: segment.color || '#1976d2',
    isDynamic: !!segment.isDynamic,
    originCountries: r.originCountries || [],
    destinationCountries: r.destinationCountries || [],
    originCities: r.originCities || [],
    destinationCities: r.destinationCities || [],
    match: r.match === 'all' ? 'all' : 'any',
    monthsBack: r.monthsBack ? String(r.monthsBack) : '',
  };
}

function rulesSummary(rules: GeoRules | null | undefined): string {
  if (!rules) return '';
  const parts: string[] = [];
  if (rules.originCountries?.length) parts.push(`fra ${rules.originCountries.join(', ')}`);
  if (rules.destinationCountries?.length) parts.push(`til ${rules.destinationCountries.join(', ')}`);
  if (rules.originCities?.length) parts.push(`fra by: ${rules.originCities.join(', ')}`);
  if (rules.destinationCities?.length) parts.push(`til by: ${rules.destinationCities.join(', ')}`);
  const joined = parts.join(rules.match === 'all' ? ' og ' : ' eller ');
  return rules.monthsBack ? `${joined} (seneste ${rules.monthsBack} mdr.)` : joined;
}

const cityLabel = (c: GeoCityOption | string) =>
  typeof c === 'string' ? c : c.country ? `${c.city} (${c.country})` : c.city;

export const SegmentManager: React.FC<{ canEdit: boolean; canDelete: boolean }> = ({ canEdit, canDelete }) => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Segment | null>(null);
  const [form, setForm] = useState<SegmentForm>(emptyForm);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const { data: segments = [], isLoading } = useQuery<Segment[]>({
    queryKey: ['segments'],
    queryFn: async () => (await settingsApi.getSegments()).data.data,
  });

  const { data: geoOptions } = useQuery<GeoOptions>({
    queryKey: ['segments', 'geo-options'],
    queryFn: async () => (await settingsApi.getSegmentGeoOptions()).data.data,
    enabled: dialogOpen,
    staleTime: 5 * 60 * 1000,
  });

  const countryCodes = useMemo(() => (geoOptions?.countries || []).map((c) => c.code), [geoOptions]);
  const countryCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of geoOptions?.countries || []) m.set(c.code, c.asOrigin + c.asDestination);
    return m;
  }, [geoOptions]);
  const originCityNames = useMemo(() => (geoOptions?.originCities || []).map((c) => c.city), [geoOptions]);
  const destinationCityNames = useMemo(() => (geoOptions?.destinationCities || []).map((c) => c.city), [geoOptions]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['segments'] });
    queryClient.invalidateQueries({ queryKey: ['customers'] });
  };

  const saveMutation = useMutation({
    mutationFn: async (payload: any) =>
      editing ? settingsApi.updateSegment(editing.id, payload) : settingsApi.createSegment(payload),
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
      setSnack({ message: editing ? 'Segment opdateret' : 'Segment oprettet', severity: 'success' });
    },
    onError: (e: any) => setError(e?.response?.data?.error || 'Kunne ikke gemme segment'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => settingsApi.deleteSegment(id),
    onSuccess: () => {
      invalidate();
      setSnack({ message: 'Segment slettet', severity: 'success' });
    },
    onError: (e: any) => setSnack({ message: e?.response?.data?.error || 'Kunne ikke slette segment', severity: 'error' }),
  });

  const applyMutation = useMutation({
    mutationFn: (id: string) => settingsApi.applySegment(id),
    onSuccess: (res) => {
      invalidate();
      const r = res.data.data || {};
      setSnack({
        message: `Segment anvendt: ${r.matched ?? 0} kunder matcher, ${r.assigned ?? 0} tilføjet, ${r.cleared ?? 0} fjernet`,
        severity: 'success',
      });
    },
    onError: (e: any) => setSnack({ message: e?.response?.data?.error || 'Kunne ikke anvende segment', severity: 'error' }),
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (segment: Segment) => {
    setEditing(segment);
    setForm(formFromSegment(segment));
    setError('');
    setDialogOpen(true);
  };

  const hasAnyRule =
    form.originCountries.length > 0 || form.destinationCountries.length > 0 ||
    form.originCities.length > 0 || form.destinationCities.length > 0;

  const handleSave = () => {
    if (!form.name.trim()) {
      setError('Navn er påkrævet');
      return;
    }
    if (form.isDynamic && !hasAnyRule) {
      setError('Et dynamisk segment skal have mindst én geografisk regel');
      return;
    }
    const geoRules: GeoRules | null = form.isDynamic
      ? {
          originCountries: form.originCountries,
          destinationCountries: form.destinationCountries,
          originCities: form.originCities,
          destinationCities: form.destinationCities,
          match: form.match,
          ...(form.monthsBack ? { monthsBack: Number(form.monthsBack) } : {}),
        }
      : null;
    saveMutation.mutate({
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      color: form.color,
      isDynamic: form.isDynamic,
      geoRules,
    });
  };

  return (
    <Card sx={{ borderRadius: 1 }}>
      <CardContent sx={{ p: '12px 14px !important' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2" fontWeight={600}>Segmenter</Typography>
          {canEdit && (
            <Button size="small" startIcon={<AddIcon />} onClick={openCreate} sx={{ borderRadius: 1 }}>
              Nyt segment
            </Button>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          Dynamiske segmenter tildeles automatisk ud fra afsender- og modtageradresser på kundens forsendelser.
          Manuelt valgte segmenter overskrives aldrig.
        </Typography>

        {isLoading ? (
          <Box display="flex" justifyContent="center" p={2}><CircularProgress size={20} /></Box>
        ) : segments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Ingen segmenter endnu</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Navn</TableCell>
                <TableCell>Regler</TableCell>
                <TableCell align="right">Kunder</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {segments.map((segment) => (
                <TableRow key={segment.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: segment.color || '#1976d2', flexShrink: 0 }} />
                      <Typography variant="body2" fontWeight={500}>{segment.name}</Typography>
                      {segment.isDynamic && (
                        <Chip size="small" icon={<GlobeIcon sx={{ fontSize: '0.8rem !important' }} />} label="Dynamisk" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                    {segment.description && (
                      <Typography variant="caption" color="text.secondary">{segment.description}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {segment.isDynamic ? rulesSummary(segment.geoRules) : 'Manuel'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{segment._count?.customers ?? 0}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {segment.isDynamic && canEdit && (
                      <Tooltip title="Anvend nu">
                        <span>
                          <IconButton size="small" onClick={() => applyMutation.mutate(segment.id)} disabled={applyMutation.isPending}>
                            <ApplyIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {canEdit && (
                      <Tooltip title="Rediger">
                        <IconButton size="small" onClick={() => openEdit(segment)}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                    {canDelete && (
                      <Tooltip title="Slet">
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (window.confirm(`Slet segmentet "${segment.name}"?`)) deleteMutation.mutate(segment.id);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem' }}>{editing ? 'Rediger segment' : 'Nyt segment'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
          <Box display="flex" flexDirection="column" gap={1.5} mt={0.5}>
            <TextField
              label="Navn"
              size="small"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              autoFocus
            />
            <TextField
              label="Beskrivelse"
              size="small"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <Box display="flex" alignItems="center" gap={1.5}>
              <TextField
                label="Farve"
                size="small"
                type="color"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                sx={{ width: 90 }}
              />
              <FormControlLabel
                control={<Switch size="small" checked={form.isDynamic} onChange={(e) => setForm({ ...form, isDynamic: e.target.checked })} />}
                label="Dynamisk geografisk segment"
              />
            </Box>

            {form.isDynamic && (
              <Box display="flex" flexDirection="column" gap={1.5} sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Kunder tildeles segmentet, når deres forsendelser matcher reglerne herunder.
                </Typography>
                <Autocomplete
                  multiple
                  size="small"
                  options={countryCodes}
                  value={form.originCountries}
                  onChange={(_, v) => setForm({ ...form, originCountries: v })}
                  getOptionLabel={(c) => `${countryLabel(c)}${countryCounts.has(c) ? ` · ${countryCounts.get(c)}` : ''}`}
                  renderInput={(params) => <TextField {...params} label="Afsenderlande" placeholder="Vælg lande" />}
                />
                <Autocomplete
                  multiple
                  size="small"
                  options={countryCodes}
                  value={form.destinationCountries}
                  onChange={(_, v) => setForm({ ...form, destinationCountries: v })}
                  getOptionLabel={(c) => `${countryLabel(c)}${countryCounts.has(c) ? ` · ${countryCounts.get(c)}` : ''}`}
                  renderInput={(params) => <TextField {...params} label="Modtagerlande" placeholder="Vælg lande" />}
                />
                <Autocomplete
                  multiple
                  freeSolo
                  size="small"
                  options={originCityNames}
                  value={form.originCities}
                  onChange={(_, v) => setForm({ ...form, originCities: v.map((x) => cityLabel(x)) })}
                  renderInput={(params) => <TextField {...params} label="Afsenderbyer" placeholder="Vælg eller skriv by" />}
                />
                <Autocomplete
                  multiple
                  freeSolo
                  size="small"
                  options={destinationCityNames}
                  value={form.destinationCities}
                  onChange={(_, v) => setForm({ ...form, destinationCities: v.map((x) => cityLabel(x)) })}
                  renderInput={(params) => <TextField {...params} label="Modtagerbyer" placeholder="Vælg eller skriv by" />}
                />
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={form.match}
                    onChange={(_, v) => v && setForm({ ...form, match: v })}
                  >
                    <ToggleButton value="any" sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Mindst én regel</ToggleButton>
                    <ToggleButton value="all" sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Alle regler</ToggleButton>
                  </ToggleButtonGroup>
                  <TextField
                    label="Kun seneste N måneder"
                    size="small"
                    type="number"
                    value={form.monthsBack}
                    onChange={(e) => setForm({ ...form, monthsBack: e.target.value })}
                    inputProps={{ min: 1, max: 240 }}
                    sx={{ width: 180 }}
                    helperText="Tom = hele historikken"
                  />
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} size="small">Annuller</Button>
          <Button onClick={handleSave} variant="contained" size="small" disabled={saveMutation.isPending} sx={{ borderRadius: 1 }}>
            {saveMutation.isPending ? 'Gemmer...' : 'Gem'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack?.severity || 'success'} onClose={() => setSnack(null)} sx={{ width: '100%' }}>
          {snack?.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default SegmentManager;
